import React from 'react';
import { Card, CardContent, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Tooltip } from '@mui/material';
import { FunnelChart, Funnel, LabelList, Cell } from 'recharts';

interface TileFunnelGraphProps {
  data: {
    page: number;
    page_name: string;
    pages_visits: number;
    sessions_count: number;
  }[];
  isTile: boolean;
}
const colors = ["#ff9999", "#ffff99", "#ccff99", "#99ff99"];

const toCamelCase = (str) => {
  return str
    .split(/([-/])/)
    .map((segment) => {
      if (segment === '-' || segment === '/') return segment;
      const camelCaseSegment = segment.toLowerCase();
      return camelCaseSegment.charAt(0).toUpperCase() + camelCaseSegment.slice(1);
    })
    .join('');
};

const FunnelView: React.FC<TileFunnelGraphProps> = ({ data, isTile }) => {
  // Get the max sessions_count for normalization
  const maxValue = Math.max(...data.map((entry) => entry.sessions_count));

  // Normalize the data based on the maximum value
  const normalizedData = data.map((entry) => ({
    ...entry,
    normalizedValue: entry.sessions_count / maxValue,
    percentage: ((entry.sessions_count / maxValue) * 100).toFixed(1),
    formattedPageName: toCamelCase(entry.page_name),
  }));

  const calculatePercentage = (value: number, total: number) => {
    return ((value / total) * 100).toFixed(1);
  };

  return (
    <Grid container spacing={2} width={'100%'}>
      {/* Funnel Chart */}
      <Grid item xs={12} sm={12} md={isTile? 12: 6} style={{ display: 'flex' }}>
        <Card style={{ flex: 1 }}>
          <CardContent style={{ display: "flex", justifyContent: "center", alignItems: "flex-end", padding: "20px", position: "relative" }}>
        
          <div className = "label" style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", marginRight: "20px" }}>
          {normalizedData.map((entry, index) => (
              <div key={index} style={{ marginBottom: '10px', fontSize: '12px', display: 'flex', alignItems: 'center', fontFamily: 'Roboto, sans-serif', color: '#292a2b' }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    backgroundColor: colors[index],
                    marginRight: '8px'
                  }}
                ></span>
                {entry.formattedPageName}: {entry.sessions_count}
              </div>
            ))}
            </div>
            
            <FunnelChart width={isTile ? 150 :400} height={isTile? 90 :300}>
              <Funnel dataKey="sessions_count" data={normalizedData} nameKey="formattedPageName" isTrapezoid={true}>
                <LabelList
                  position="inside"
                  dataKey="formattedPageName"
                  formatter={(value, entry, index) => {
                    const percentage = normalizedData[index].percentage;
                    const sessions = normalizedData[index].sessions_count;
                    return `${value} : ${sessions} \n(${percentage}%)`; // Display name, sessions, and percentage on new lines
                  }}
                  content={({ x, y, width, height, value, index }) => (
                    <>{
                      !isTile &&
                    <Tooltip
                    key={normalizedData[index].page_name} // Unique key for each Tooltip
                    // title={`Page: ${normalizedData[index].page_name}\nSessions: ${normalizedData[index].sessions_count}\nPercentage: ${normalizedData[index].percentage}%`}
                    title={
                      <div style={{ 
                        backgroundColor: 'white', // White background for title
                        color: 'black', // Black text
                        fontSize: '10px', // Smaller font size
                        padding: '10px', // Compact padding
                        borderRadius: '4px' // Rounded corners
                      }}>
                        {`Page: ${toCamelCase(normalizedData[index].page_name)}`}<br />
                        {`Sessions: ${normalizedData[index].sessions_count}`}<br />
                        {`Percentage: ${normalizedData[index].percentage}%`}
                        {/* {`Page: ${normalizedData[index].page_name}\nSessions: ${normalizedData[index].sessions_count}\nPercentage: ${normalizedData[index].percentage}%`} */}
                      </div>
                    }
                    arrow
                  >
                    <text
                      x={x + width / 2}
                      y={y + height / 2}
                      textAnchor="middle"
                      fill="#000" // Change the text color here
                      fontSize="10"
                      dominantBaseline="middle" // Ensure the text is centered vertically
                    >
                      {`${normalizedData[index].formattedPageName} : ${normalizedData[index].sessions_count} \n(${normalizedData[index].percentage}%)`}
                    </text>
                  </Tooltip>}
                  </>
                    
                  )}
                />
                
                {/* Apply different colors to each funnel section */}
                {
                  colors.map(item => <Cell fill={item} stroke="#000" strokeWidth={0.5} />)
                }
              </Funnel>
            </FunnelChart>
          </CardContent>
        </Card>
      </Grid>

      {/* Table with Page Visits and Sessions */}
      {!isTile && (
        <Grid item xs={12} sm={12} md={6} style={{ display: 'flex' }}>
          <Card style={{ flex: 1 }}>
            <CardContent style={{height: '100%'}}>
              <Typography variant="h8" gutterBottom>
                Page Visits and Sessions
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Page</TableCell>
                      <TableCell>Page Visits</TableCell>
                      <TableCell>Sessions</TableCell>
                      <TableCell>Percentage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {normalizedData.map((row) => (
                      <TableRow key={row.page}>
                        <TableCell>{row.formattedPageName}</TableCell>
                        <TableCell>{row.pages_visits}</TableCell>
                        <TableCell>{row.sessions_count}</TableCell>
                        <TableCell>{row.percentage}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default FunnelView;
